import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Typography,
  LinearProgress, Box, Alert, IconButton, FormControl, MenuItem, Select, InputLabel,
  Link, Tooltip
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import { ClassNameMap, withStyles } from '@mui/styles'
import stylesheet from '../detail-container.stylesheet'
import { PatientService } from '../../../../services/patient.service'
import { toast } from 'react-toastify'
import Helper from '../../../../utils/helper'
import MedicationEvaluationAccordionComponent from './medication-evaluation-accordion.component'
import { ProgramEvaluationResponse, MedicationEvaluationResult } from '../../../../model/evaluation.types'

interface IProps {
  classes: ClassNameMap
  open: boolean
  onClose: () => void
  patientId: string
  programName: string
  programDisplayName?: string
  carePlanId: number
  onApprovalComplete: (actionLabel?: string) => void
  onEvaluationRun?: () => void  // Called after evaluation runs to refresh parent view
}

const ProgramEvaluationDialogComponent: React.FC<IProps> = ({
  classes,
  open,
  onClose,
  patientId,
  programName,
  programDisplayName,
  carePlanId,
  onApprovalComplete,
  onEvaluationRun
}) => {
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [evaluationData, setEvaluationData] = useState<ProgramEvaluationResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [approvalStatus, setApprovalStatus] = useState<'Approved' | 'Rejected' | null>(null)
  const [selectedAction, setSelectedAction] = useState<string>('')
  const patientService = new PatientService()

  // Contextually filter reject next-action options based on evaluation state
  const filteredNextActions = useMemo((): Record<string, string> => {
    if (!evaluationData) return {}
    const actions: Record<string, string> = {
      'ext_int': 'extend evaluation period by one titration interval',
    }
    if (evaluationData.toxicity_gate_active) {
      if (evaluationData.medication_evaluations.length > 1) {
        actions['continue_tox_gate'] = 'continue checking other medications for toxicity'
      }
      actions['skip_sch_tox'] = 'skip toxicity, proceed to efficacy evaluation'
    }
    return actions
  }, [evaluationData])

  // Confirmation messages keyed by reject next-action (for toast)
  const rejectMessages: Record<string, string> = {
    'ext_int': 'Evaluation period extended by one titration interval',
    'continue_tox_gate': 'Continuing toxicity evaluation for remaining medications',
    'skip_sch_tox': 'Skipping toxicity check, proceeding to efficacy evaluation'
  }

  // Short labels for the care plan card (shown after dialog closes)
  const rejectCardLabels: Record<string, string> = {
    'ext_int': 'interval extended',
    'continue_tox_gate': 'tox gate - next med',
    'skip_sch_tox': 'tox skipped'
  }

  useEffect(() => {
    if (open && carePlanId) {
      runProgramEvaluation()
    }
  }, [open, carePlanId])

  // Reset selectedAction if it's no longer in the filtered options (e.g., after re-run)
  useEffect(() => {
    if (selectedAction && !filteredNextActions[selectedAction]) {
      setSelectedAction('')
    }
  }, [filteredNextActions, selectedAction])

  const runProgramEvaluation = async () => {
    setLoading(true)
    setError(null)
    // Reset approval state — evaluation results may have changed
    setApprovalStatus(null)
    setSelectedAction('')
    try {
      const response = await patientService.runProgramEvaluationBulk(carePlanId)
      setEvaluationData(response.data)
      // Refresh parent view to show updated care plan instructions/recommendations
      if (onEvaluationRun) {
        onEvaluationRun()
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error?.message || err?.message || 'Failed to run evaluation'
      setError(errorMsg)
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
    } finally {
      setLoading(false)
    }
  }

  const handleApproveAll = async () => {
    if (!evaluationData) return

    setSubmitting(true)
    try {
      // Use medication_ids_to_approve from backend (handles toxicity gate correctly)
      // Falls back to all medication IDs if not provided or empty
      const medicationIds = evaluationData.medication_ids_to_approve?.length
        ? evaluationData.medication_ids_to_approve
        : evaluationData.medication_evaluations.map(m => m.medication_id)
      await patientService.approveAllMedicationEvaluations(
        patientId,
        programName,
        {
          care_plan_id: carePlanId,
          medication_ids: medicationIds,
          action: 'approve'
        }
      )
      if (evaluationData.toxicity_gate_active) {
        toast.success('Symptom evaluation created. Complete it in the Symptom Evaluations section below.', Helper.bottom_center())
      } else {
        toast.success('Evaluation approved', Helper.bottom_center())
      }
      const approveLabel = evaluationData.toxicity_gate_active ? 'tox evaluation accepted' : 'approved'
      onApprovalComplete(approveLabel)
      onClose()
    } catch (err) {
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
    } finally {
      setSubmitting(false)
    }
  }

  const handleReject = async () => {
    if (!evaluationData) return

    // For rejection, we need the next_action to be selected
    if (!selectedAction) {
      toast.warning('Please select a next action for rejection', Helper.bottom_center())
      return
    }

    setSubmitting(true)
    try {
      // Get the toxic medication(s) that need to be rejected
      // In toxicity gate mode, this is the first unaddressed toxic med
      const toxicMeds = evaluationData.medication_evaluations.filter(
        m => m.evaluation_status === 'failed' || m.toxicity_evaluation === 'toxic'
      )
      const medicationIds = toxicMeds.length > 0
        ? [toxicMeds[0].medication_id]  // Reject one at a time in toxicity gate flow
        : evaluationData.medication_ids_to_approve?.length
          ? evaluationData.medication_ids_to_approve
          : evaluationData.medication_evaluations.map(m => m.medication_id)

      await patientService.approveAllMedicationEvaluations(
        patientId,
        programName,
        {
          care_plan_id: carePlanId,
          medication_ids: medicationIds,
          action: 'reject',
          next_action: selectedAction as 'ext_int' | 'continue_tox_gate' | 'skip_sch_tox'
        }
      )

      toast.success(rejectMessages[selectedAction] || 'Rejection processed', Helper.bottom_center())
      onApprovalComplete(rejectCardLabels[selectedAction] || 'rejected')

      if (selectedAction === 'continue_tox_gate') {
        // Stay open and re-run to show next medication
        setApprovalStatus(null)
        setSelectedAction('')
        await runProgramEvaluation()
      } else {
        // ext_int / skip_sch_tox — nothing more to do, close the dialog
        onClose()
      }
    } catch (err) {
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
    } finally {
      setSubmitting(false)
    }
  }

  const getSummaryIcon = () => {
    if (!evaluationData?.summary) return null
    const { status } = evaluationData.summary
    switch (status) {
      case 'all_passed':
        return <CheckCircleIcon sx={{ color: 'var(--color-success)', fontSize: '1.5rem' }} />
      case 'has_failures':
      case 'toxicity_gate':
        return <ErrorIcon sx={{ color: 'var(--color-error)', fontSize: '1.5rem' }} />
      case 'partial_pass':
        // partial_pass means meds need attention (normal case for approval)
        return <WarningIcon sx={{ color: 'var(--color-warning)', fontSize: '1.5rem' }} />
      default:
        return null
    }
  }

  const getSummaryText = () => {
    if (!evaluationData?.summary) return ''
    const { total, passed, failed, needs_attention } = evaluationData.summary
    const parts = []
    if (passed > 0) parts.push(`${passed} passed`)
    if (failed > 0) parts.push(`${failed} failed`)
    if (needs_attention > 0) parts.push(`${needs_attention} needs attention`)
    return `${total} medications evaluated: ${parts.join(', ')}`
  }

  const canApprove = () => {
    if (!evaluationData) return false
    // Use backend can_approve flag, or fall back to checking needs_attention
    if (evaluationData.can_approve !== undefined) {
      return evaluationData.can_approve
    }
    // Fallback: allow approval if there are meds needing attention (to approve recommendations)
    return evaluationData.summary?.needs_attention > 0
  }

  const getApproveDisabledReason = (): string => {
    if (!evaluationData || canApprove()) return ''
    if (evaluationData.toxicity_gate_active) {
      return 'Toxicity must be addressed before approval is available'
    }
    if (evaluationData.summary?.failed > 0) {
      return 'Evaluation has failures that must be addressed first'
    }
    return 'Approval is not available for the current evaluation results'
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh', maxHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            Program Evaluation - {programDisplayName || programName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {loading && <LinearProgress />}

      <DialogContent dividers sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Toxicity Gate Alert */}
        {evaluationData?.toxicity_gate_active && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Toxicity Detected
            </Typography>
            <Typography variant="body2">
              Address the toxicity for the highlighted medication before proceeding with other evaluations.
            </Typography>
          </Alert>
        )}

        {evaluationData && (
          <>
            {/* Summary Section with Re-run button */}
            <Box
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: 'var(--color-paper)',
                borderRadius: 1,
                border: '1px solid var(--color-border-light)'
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  {getSummaryIcon()}
                </Grid>
                <Grid item xs>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {getSummaryText()}
                  </Typography>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={runProgramEvaluation}
                    disabled={loading}
                  >
                    Re-run Evaluation
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* Show each medication as a separate accordion */}
            <Box sx={{ mb: 2 }}>
              {evaluationData.medication_evaluations?.map((med: MedicationEvaluationResult, index: number) => (
                <MedicationEvaluationAccordionComponent
                  key={med.medication_id}
                  classes={classes}
                  evaluation={med}
                  defaultExpanded={index === 0 || med.evaluation_status === 'failed'}
                />
              ))}
            </Box>

            {/* Action buttons matching individual med card flow */}
            <Box className={classes.subModalBackground} sx={{ p: 2, mt: 2 }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item>
                  <Tooltip title={getApproveDisabledReason()} arrow>
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setApprovalStatus('Approved')}
                        disabled={!canApprove() || submitting || loading}
                        className={approvalStatus === 'Approved' ? classes.dialogBtnActionSelected : classes.dialogBtnAction}
                        sx={approvalStatus === 'Approved' ? {
                          backgroundColor: 'var(--color-btn-selected-bg) !important',
                          color: 'var(--color-btn-selected-text) !important',
                          borderColor: 'var(--color-btn-selected-border) !important',
                        } : undefined}
                      >
                        Approve
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setApprovalStatus('Rejected')}
                    disabled={submitting || loading}
                    className={approvalStatus === 'Rejected' ? classes.dialogBtnActionSelected : classes.dialogBtnAction}
                    sx={approvalStatus === 'Rejected' ? {
                      backgroundColor: 'var(--color-btn-selected-bg) !important',
                      color: 'var(--color-btn-selected-text) !important',
                      borderColor: 'var(--color-btn-selected-border) !important',
                    } : undefined}
                  >
                    Reject
                  </Button>
                </Grid>
                <Grid item>
                  <FormControl className={classes.formControlStage}>
                    <InputLabel id="next-action-label" className={classes.stageLabel}
                      sx={{ color: approvalStatus !== 'Rejected' ? 'gray' : 'var(--color-primary-dark)' }}
                    >
                      Next action
                    </InputLabel>
                    <Select
                      id="next-action"
                      labelId="next-action-label"
                      value={selectedAction}
                      onChange={(e) => setSelectedAction(e.target.value)}
                      disabled={approvalStatus !== 'Rejected'}
                      label="Next action"
                      sx={{
                        width: '6.8rem',
                        paddingTop: '0px',
                        fontSize: '0.7rem',
                        '&:before, &:after, & .MuiOutlinedInput-notchedOutline': {
                          border: 'none !important',
                        },
                      }}
                      className={classes.nextActionSelect}
                    >
                      {Object.keys(filteredNextActions).map((key) => (
                        <MenuItem key={key} value={key} className={classes.actionMenu}>
                          {filteredNextActions[key]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* Alternative recommendations link */}
            <Box sx={{ mt: 1 }}>
              <Link
                component="button"
                variant="body2"
                sx={{ color: 'var(--color-warning)', cursor: 'pointer' }}
                onClick={() => {
                  // TODO: Implement alternative recommendations
                  toast.info('Alternative recommendations feature coming soon', Helper.bottom_center())
                }}
              >
                Alternative recommendations
              </Link>
            </Box>
          </>
        )}

        {!loading && !evaluationData && !error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="textSecondary">
              Click "Run Evaluation" to evaluate all medications
            </Typography>
            <Button
              variant="contained"
              onClick={runProgramEvaluation}
              sx={{ mt: 2 }}
            >
              Run Evaluation
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="contained"
          onClick={() => {
            if (approvalStatus === 'Approved') {
              handleApproveAll()
            } else if (approvalStatus === 'Rejected') {
              handleReject()
            }
          }}
          disabled={!approvalStatus || submitting || loading || (approvalStatus === 'Rejected' && !selectedAction)}
          color="primary"
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </Button>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default withStyles(stylesheet)(ProgramEvaluationDialogComponent)
